import * as _ from "lodash";
import {IHierarchyNode} from "../PA.Core.IF.Hierarchy";
import {IHierarchyStructure, INode, INodeLogicalKey} from "../../PA.MD.Core/PA.MD.IF.Hierarchy";
import {sha1} from "object-hash";
import {NumberUtils} from "./Numbers";
import {EntityCache} from "../../PA.MD.Entity/EntityCache";
import {EntityTypeUtils} from "../../PA.MD.Entity/EntityTypeUtils";
import {IEntity} from "../../PA.MD.Core/PA.MD.IF.Entity";

export interface IHierarchyNodeDescriptor {
    e_cache: EntityCache,
    e_cache_ent: EntityCache,
    logicalKeyOrder: {id: string, name: string}[]
}

export interface INodeDescription {
    IdCSV: string,
    NameIdCSV: string,
    NameCSV: string,
    Errors?: string[]
}

export class HierarchyUtils {

    static pruneHierarchy(dctNodes: { [nodeId: string]: IHierarchyNode }, nodeIds: string[], bIncludeChildren: boolean, bIncludeParents: boolean)
        : { [nodeId: string]: IHierarchyNode } {
        let rv: { [nodeId: string]: IHierarchyNode } = {}

        if (_.isEmpty(nodeIds))
            return {}

        // Create dictionary of children, by parent id
        let dctChildren: { [nodeId: string]: IHierarchyNode[] } = {}

        _.forEach(_.values(dctNodes), (aNode) => {
            let parentId = aNode?.ParentId
            if (_.isEmpty(parentId))
                return

            if (parentId in dctChildren)
                dctChildren[parentId].push(aNode)
            else
                dctChildren[parentId] = [aNode]
        })

        let dctProcessedChildren: { [id: string]: boolean } = {}
        let dctProcessedParent: { [id: string]: boolean } = {}

        function addParent(parentId: string) {
            // Recurse up
            if (_.isEmpty(parentId))
                return

            // If the parent node isn't known, mark processed and bail
            if (!(parentId in dctNodes)) {
                dctProcessedParent[parentId] = true
                return
            }

            if (parentId in dctProcessedParent)
                return

            dctProcessedParent[parentId] = true

            let parent = dctNodes[parentId]
            if (!(parentId in rv))
                rv[parentId] = parent

            // recurse up hierarchy
            addParent(parent.ParentId)

        }

        function addChildren(aNode: IHierarchyNode) {
            // Add each Child
            if (_.isEmpty(aNode))
                return

            if (aNode.NodeId in dctProcessedChildren)
                return

            dctProcessedChildren[aNode.NodeId] = true

            if (!(aNode.NodeId in dctChildren))
                return

            let children = dctChildren[aNode.NodeId]
            // Add children to returnValue
            _.forEach(children, (aChild) => {
                if (!(aChild.NodeId in rv)) {
                    rv[aChild.NodeId] = aChild
                }
            })

            //-- Recurse to add children
            _.forEach(children, (aChild) => {
                addChildren(aChild)
            })


        }


        _.forEach(nodeIds, (aNodeId) => {
            if (!(aNodeId in dctNodes))
                return

            let node = dctNodes[aNodeId]
            if (!(aNodeId in rv))
                rv[aNodeId] = node

            if (bIncludeParents)
                addParent(node.ParentId)

            if (bIncludeChildren)
                addChildren(node)

        })


        return rv

    }


    static getLogicKeyHash(logicalKey: INodeLogicalKey): string {
        if (_.isEmpty(logicalKey))
            return undefined

        let sError = undefined
        let hashObj: INodeLogicalKey = {}
        _.forEach(logicalKey, (value, key) => {
            let cLK: typeof hashObj.a = {
                keyId: value.keyId,
                dataType: value.dataType
            }
            switch (cLK.dataType) {
                case "entity":
                    cLK.valueWbId = value.valueWbId
                    cLK.valueWbEntityId = value.valueWbEntityId
                    break;
                case "string":
                    cLK.valueString = value.valueString
                    break;
                case "number":
                    cLK.valueNumber = value.valueNumber
                    break;
                default:
                    sError = `Unknown DataType ${cLK.dataType}`
                    return false
            }

            hashObj[key] = cLK
        })

        if (sError)
            return undefined

        return sha1(hashObj)

    }

    static async getHierarchyNodeDescriptor(hs: IHierarchyStructure,
                                            e_cache: EntityCache,
                                            e_cache_ent: EntityCache): Promise<IHierarchyNodeDescriptor>
    {
        let rv: IHierarchyNodeDescriptor = {
            e_cache: e_cache,
            e_cache_ent: e_cache_ent,
            logicalKeyOrder: []
        }

        let dctKeyId: {[id:string]: string} = {}
        // Create Key Top Down
        // Enterprise Entity First, Then Attribute Else Entity
        if(hs.bHasLevels && hs.bHasClassificationNodes)
            for (let lvl of _.orderBy(hs.Levels, (a) => a.levelDepth)) {
                if (lvl.levelEnterpriseEntityTypeId > 0){
                    let et_ent = await EntityTypeUtils.getEntityTypeRow(lvl.levelEnterpriseEntityTypeId)
                    let keyIdEnt = et_ent?.cif?.ENTITY_ID || 'UnknownEnterpriseEntityType'
                    let keyNameEnt = et_ent?.cif?.ENTITY_NAME || 'Unknown'
                    if(!(keyIdEnt in dctKeyId)){
                        dctKeyId[keyIdEnt] = keyIdEnt
                        rv.logicalKeyOrder.push({id: keyIdEnt, name: keyNameEnt})
                    }

                    // Determine if Entity Attribute or Entity
                    let et = await EntityTypeUtils.getEntityTypeRow(lvl.levelEntityTypeId)
                    if(lvl.levelEntityAttributeId > 0){
                        let attrM = await EntityTypeUtils.getAttributeType(lvl.levelEntityAttributeId)
                        let keyId = `${et?.cif?.ENTITY_ID}.${attrM?.cif?.ATTRIBUTEM_ID}`
                        let keyName = `${et?.cif?.ENTITY_NAME} ${attrM?.cif?.ATTRIBUTEM_NAME}`
                        if(!(keyId in dctKeyId)){
                            dctKeyId[keyId] = keyId
                            rv.logicalKeyOrder.push({id: keyId, name: keyName})
                        }
                    } else {
                        let keyId = et?.cif?.ENTITY_ID
                        let keyName = et?.cif?.ENTITY_NAME
                        if(!(keyId in dctKeyId)){
                            dctKeyId[keyId] = keyId
                            rv.logicalKeyOrder.push({id: keyId, name: keyName})
                        }

                    }
                }
            }

        return rv
    }

    static getEntityDescription(node: IEntity, descr: IHierarchyNodeDescriptor): INodeDescription{
        return {
            IdCSV: node?.id_ui || "",
            NameIdCSV: `${node?.name || ""} (${node?.id_ui || ""})`,
            NameCSV: node?.name || ""
        }

    }

    static async getLogicalKeyDescription(lk: INodeLogicalKey, descr: IHierarchyNodeDescriptor): Promise<INodeDescription>{
        let errors: string[] = []

        let r: {id: string, name: string}[] = []
        for(let key of descr?.logicalKeyOrder || []){
            if(!(key.id in lk))
                continue

            let id = ""
            let name = ""

            let lkc = lk[key.id]
            switch (lkc.dataType){
                case "entity":
                    let entity: IEntity = undefined
                    if(key.id in {'Company':1,'SalesOrg':2,'SalesArea':3}) {
                        if (descr.e_cache_ent)
                            entity = await descr.e_cache_ent.getEntity(lkc.valueWbEntityId, lkc.valueWbId)
                    } else {
                        if(descr.e_cache)
                            entity = await descr.e_cache.getEntity(lkc.valueWbEntityId, lkc.valueWbId)
                    }
                    if(_.isEmpty(entity)){
                        errors.push(`Unable to resolve Entity for Key ${key.name} (${key.id})`)
                    } else {
                        id = entity.id_ui
                        name = entity.name
                    }
                    break;
                case "string":
                    name = key.name
                    id = lkc.valueString
                    break;
                case "number":
                    name = key.name
                    id = '' + lkc.valueNumber
                    break;
                default:
                    id = '-'
                    name = 'Unknown'
                    break;
            }

            r.push({id: id, name: name})
        }

        let rv: INodeDescription =  {
            IdCSV: _.map(r, (a) => a.id).join(","),
            NameCSV: _.map(r, (a) => a.name).join(","),
            NameIdCSV: _.map(r, (a) => `${a.name} (${a.id})`).join(",")
        }
        if(!_.isEmpty(errors))
            rv.Errors = errors
        return rv

    }

    static logicalKeyEquals(a: INodeLogicalKey, b: INodeLogicalKey): boolean {
        if (_.isEmpty(a))
            return false
        if (_.isEmpty(b))
            return false

        if (HierarchyUtils.compareLKEqualSub(a, b))
            return HierarchyUtils.compareLKEqualSub(b, a)

        return false
    }

    private static compareLKEqualSub(a: INodeLogicalKey, b: INodeLogicalKey): boolean {

        for (let aKey in a) {
            if (!(aKey in b))
                return false
            let eA = a[aKey]
            let eB = b[aKey]
            if (eA.dataType != eB.dataType)
                return false

            switch (eA.dataType) {
                case "entity":
                    if (eA.valueWbId != eB.valueWbId)
                        return false
                    if (eA.valueWbEntityId != eB.valueWbEntityId)
                        return false
                    break
                case "string":
                    if (eA.valueString != eB.valueString)
                        return false
                    break
                case "number":
                    if (eA.valueNumber == eB.valueNumber)
                        break
                    if (NumberUtils.round(eA.valueNumber, 6)
                        == NumberUtils.round(eB.valueNumber, 6))
                        break
                    return false
                default:
                    return false

            }
        } // Next Key
        return true
    }


}